// externals
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';

// components
import VinInput from '@cx/ui/VinInput';
import { BarCodeSolidIcon } from '@interstate/components/Icons';

// selectors
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { deferredSelect, useSelectOnFocusEnabled } from '@makemydeal/dr-shared-components/select-on-focus';
import { useSingleTradeInFormProvider } from './MultiTradeIn/TradeInFormProvider';
// constants
import { VIN } from '../../constants';

// actions
import type { RSAAAction } from 'redux-api-middleware';

import { TradeDecode, TradeMultipleVIN, TradeVinDecodeContainer, BarcodePlaceholder } from './trade.style';
import type { FetchVinDecodeResponse } from '@makemydeal/dr-shared-types';
import { MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { Action } from '@interstate/components/Action';
import { Typography } from '@interstate/components/Typography';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { isVinValid } from '../../utils/VinValidations';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { updateTradeInErrorState } from './lienholderForm/utils';

interface TradeVinDecodeProps {
    isManualFormOpen: boolean;
    disabled?: boolean;
}

const TradeVinDecode: React.FC<TradeVinDecodeProps> = ({ isManualFormOpen, disabled }) => {
    const [enableDecode, setEnableDecode] = React.useState(false);
    const [isValidVIN, setIsValidVin] = useState(false);
    const [duplicateVin, setDuplicateVin] = useState(false);
    const isInitialAutoFocusRef = useRef(true);

    const dispatch = useDispatch();
    const { tradeInIndex } = useSingleTradeInFormProvider();
    const vin = useSelector(tradeInsSelectors.getVin(tradeInIndex));
    const vehicleYear = useSelector(tradeInsSelectors.getTradeYear(tradeInIndex));
    const vehicleMake = useSelector(tradeInsSelectors.getTradeMake(tradeInIndex));
    const vehicleModel = useSelector(tradeInsSelectors.getTradeModel(tradeInIndex));
    const vehicleTrim = useSelector(tradeInsSelectors.getTradeTrim(tradeInIndex));
    const tradeIns = useSelector(tradeInsSelectors.getAllTradeIns);
    const hasMultipleVin = useSelector(tradeInsSelectors.hasMultipleVin(tradeInIndex));
    const multipleVinOptions = useSelector(tradeInsSelectors.multipleVinOptions(tradeInIndex));
    const isDecodeDisabled = !enableDecode || disabled;
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isPreserveCrmTradeVehicleInfoEnabled = useSelector(featureToggleSelectors.isPreserveCrmTradeVehicleInfoEnabled);
    const isAutoSelectEnabled = useSelectOnFocusEnabled(featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap);
    const tradeInErrors = useSelector((state: StateTree) =>
        dealScreenErrorSelectors.getCategoryValidationErrors(state, DealTabs.TRADE_IN, DealTabs.TRADE_IN)
    );
    const vinValidationState = (tradeInErrors[tradeInIndex] || {})[VIN] || false;

    const hasIncompleteVehicleInfo = useMemo(() => {
        const isValidYear = Boolean(vehicleYear) && !isNaN(Number(vehicleYear)) && Number(vehicleYear) > 0;
        const isValidValue = (value: string) => {
            const trimmedValue = value?.trim() || '';
            return trimmedValue.length > 0 && trimmedValue.toLowerCase() !== 'default';
        };

        return !isValidYear || !isValidValue(vehicleMake) || !isValidValue(vehicleModel) || !isValidValue(vehicleTrim);
    }, [vehicleYear, vehicleMake, vehicleModel, vehicleTrim]);

    const appliedVins = useMemo(() => {
        return (tradeIns || [])
            .filter((trade, index) => index !== tradeInIndex && !trade.isDeleted && trade.tradeIn?.vin)
            .map((trade) => trade.tradeIn.vin);
    }, [tradeIns, tradeInIndex]);

    useEffect(() => {
        setIsValidVin(vin ? vin.length === 17 : false);
    }, [vin]);

    useEffect(() => {
        setEnableDecode(isValidVIN);
    }, [isValidVIN]);

    useEffect(() => {
        if (!isDealScreenRedDotErrorsEnabled) return;

        const hasInvalidVin = Boolean(vin && !isVinValid(vin)) || duplicateVin;

        if (vinValidationState !== hasInvalidVin) {
            const updatedErrorState = updateTradeInErrorState(tradeInErrors, tradeInIndex, { [VIN]: hasInvalidVin });
            dispatch(
                dealScreenErrorActionCreators.setErrorFieldsByCategory(DealTabs.TRADE_IN, DealTabs.TRADE_IN, updatedErrorState)
            );
        }
    }, [vin, dispatch, tradeInIndex, duplicateVin]);

    const onVinChange = (cxEvent: React.ChangeEvent<HTMLInputElement>) => {
        const value = cxEvent.target.value.toUpperCase();
        const isValid = value.length === 17;
        const isVINDuplicate = appliedVins.includes(value);
        setDuplicateVin(isVINDuplicate);
        if (isVINDuplicate) {
            dispatch(tradeInsActionCreators.updateTradeInVin('', tradeInIndex));
            return;
        }
        dispatch(tradeInsActionCreators.updateTradeInVin(value, tradeInIndex));
        setIsValidVin(isValid);
    };

    const onDecodeClick = () => {
        dispatch(
            tradeInsActionCreators.vinDecodeRSAA(
                MANAGER_VIEW_APP_PREFIX,
                vin,
                tradeInIndex,
                isPreserveCrmTradeVehicleInfoEnabled
            ) as unknown as RSAAAction<any, unknown, unknown>
        );
    };

    const debouncedDecodeClick = useCallback(debounce(onDecodeClick, 0), [vin, tradeInIndex, isPreserveCrmTradeVehicleInfoEnabled]);

    useEffect(() => {
        return () => {
            debouncedDecodeClick.cancel();
        };
    }, [debouncedDecodeClick]);

    const selectMultipleOption = (optionSelected: FetchVinDecodeResponse[0]) => {
        dispatch(
            tradeInsActionCreators.selectMultipleVinOption(optionSelected, tradeInIndex, isPreserveCrmTradeVehicleInfoEnabled)
        );
    };

    const handleBlur = () => {
        if (isValidVIN && (!isPreserveCrmTradeVehicleInfoEnabled || hasIncompleteVehicleInfo)) {
            debouncedDecodeClick();
        }
    };

    return (
        <>
            {isManualFormOpen && (
                <TradeVinDecodeContainer id="trade-vin-decode" disabled={disabled}>
                    {
                        <TradeDecode>
                            <Action
                                data-testid="vin-decode-btn"
                                onClick={() => {
                                    debouncedDecodeClick();
                                }}
                                disabled={isDecodeDisabled}
                            >
                                <Typography
                                    variant="anchor-block-xs"
                                    color={isDecodeDisabled ? 'base.color.gray.500' : 'base.color.blue.500'}
                                >
                                    Decode
                                </Typography>
                            </Action>
                        </TradeDecode>
                    }
                    {
                        <label
                            style={{ fontSize: '0.857rem', margin: 0 }}
                            data-testid="vin-decode-btn-label"
                            htmlFor={'vin-decode-btn'}
                        >
                            {VIN}
                        </label>
                    }
                    <VinInput
                        style={{
                            height: '2.3rem',
                            borderRadius: '4px',
                            border: '1px solid #959595'
                        }}
                        data-testid={`vin-decode-input-${tradeInIndex}`}
                        htmlId={`vin-decode-input-${tradeInIndex}`}
                        displayLabel={false}
                        label={VIN}
                        name="vin-decode-input"
                        onChange={onVinChange}
                        onBlur={handleBlur}
                        onFocus={() => {
                            if (isInitialAutoFocusRef.current) {
                                isInitialAutoFocusRef.current = false;
                                return;
                            }
                            // @cx/ui's TextInput calls its onFocus with no event, so there's no
                            // FocusEvent to read the target from — reach the input by its htmlId instead.
                            if (isAutoSelectEnabled) {
                                const el = document.getElementById(`vin-decode-input-${tradeInIndex}`) as HTMLInputElement | null;
                                if (el) deferredSelect(el);
                            }
                        }}
                        value={vin}
                        disabled={disabled}
                        autoFocus
                        error={duplicateVin ? 'This VIN is already in use. Enter a unique VIN.' : undefined}
                    />
                    {!vin && !duplicateVin && (
                        <BarcodePlaceholder data-testid="barcode-placeholder">
                            <BarCodeSolidIcon width={14} height={14} />
                            <span>VIN INPUT</span>
                        </BarcodePlaceholder>
                    )}
                    {hasMultipleVin && multipleVinOptions.length > 0 && (
                        <TradeMultipleVIN data-testid="multiple-vin-options-container">
                            {multipleVinOptions.map((option) => (
                                <p
                                    key={option.id}
                                    data-testid={`multiple-option-${option.id}`}
                                    onClick={() => selectMultipleOption(option)}
                                >
                                    {option.title}
                                </p>
                            ))}
                        </TradeMultipleVIN>
                    )}
                </TradeVinDecodeContainer>
            )}
        </>
    );
};

export default TradeVinDecode;
